import {CompetitorRegistrationDto} from "./race-registration/competitor-registration-dto";
import {RaceCommercialItem} from "./race-commercial-item";
import {RaceAdditionalPricingItem} from "./race-additional-pricing-item";
import {CommercialItemDiscount} from "./race-registration/commercial-item-discount";
import {AdditionalItemDiscount} from "./race-registration/additional-item-discount";
import {RegisterToRaceComponent} from "@pages";
import {RaceSettings} from "./race-settings";
import {getTeamMemberCount, TeamType} from "./team-type";

export enum RegisterToRaceComponentState {
  INITIAL,
  ERROR
}

export enum RegisterToRaceUserType {
  PREVIEW,
  ORGANISER,
  MEMBER,
  GUEST
}

export enum RegisterToRaceFormType {
  REGISTRATION_INFO,
  CONFIRMATION,
  PAYMENT,
  REGISTRATION_WITHOUT_PAYMENT_SUCCESS,
}

export class RegisterHelper {
  registrations: CompetitorRegistrationDto[] = [new CompetitorRegistrationDto()];
  raceCommercialItems: RaceCommercialItem[] = [];
  raceAdditionalItems: RaceAdditionalPricingItem[] = [];
  currentRaceCommercialItem?: RaceCommercialItem;
  registerToRaceComponent?: RegisterToRaceComponent;
  // in case of team registration this member holds calculated registration fee price
  teamCommercialItemWithDiscount?: CommercialItemDiscount;
  raceSettings?: RaceSettings;

  constructor(raceCommercialItems: RaceCommercialItem[], raceAdditionalItems: RaceAdditionalPricingItem[], registerToRaceComponent?: RegisterToRaceComponent, raceSettings?: RaceSettings) {
    this.raceCommercialItems = raceCommercialItems;
    this.raceAdditionalItems = raceAdditionalItems;
    this.registerToRaceComponent = registerToRaceComponent;
    this.raceSettings = raceSettings;
    if (this.isTeamRegistration()) {
      this.registrations = [];
      for (let i = 0; i < (getTeamMemberCount(this.raceSettings?.teamType)); i++) {
        this.registrations.push(new CompetitorRegistrationDto());
      }
    }
    if ((this.raceCommercialItems.length ?? 0) === 1) {
      this.currentRaceCommercialItem = this.raceCommercialItems[0];
      this.registrations.forEach((item: CompetitorRegistrationDto) => item.raceCommercialItem = this.currentRaceCommercialItem);
    }
  }

  onChangeFirstName(firstName: string, index: number) {
    this.registrations[index].firstName = firstName;
    this.registerToRaceComponent?.updateSummaryCompetitorItems();
  }

  onChangeLastName(lastName: string, index: number) {
    this.registrations[index].lastName = lastName;
    this.registerToRaceComponent?.updateSummaryCompetitorItems();
  }

  onChangeRegistrationFeeId(registrationFeeId: number, index: number): void {
    if (this.raceCommercialItems && this.raceCommercialItems.length > 1) {
      let raceCommercialItem: RaceCommercialItem | undefined = this.raceCommercialItems.find((item: RaceCommercialItem) => item.id === registrationFeeId);
      if (raceCommercialItem) {
        this.currentRaceCommercialItem = raceCommercialItem;
        this.registrations.forEach((item: CompetitorRegistrationDto) => item.raceCommercialItem = this.currentRaceCommercialItem);
      }
    }
    this.registerToRaceComponent?.updateSummaryCompetitorItems();
    this.registerToRaceComponent?.recalculatePrices();
  }

  onChangeRaceAdditionalItems(ids: {
    raceAdditionalPricingItem: number;
    racePricingItemValue: number | undefined,
    addWithoutPricingItemValues: boolean
  }[], index: number) {
    let raceAdditionalItems: RaceAdditionalPricingItem[] = [];
    for (let idItem of ids) {

      let raceAdditionalPricingItem: RaceAdditionalPricingItem | undefined = this.raceAdditionalItems.find((item: RaceAdditionalPricingItem) => item.id === idItem.raceAdditionalPricingItem);
      if (raceAdditionalPricingItem) {
        if (!idItem.addWithoutPricingItemValues) {
          let pricingItem: RaceAdditionalPricingItem | undefined = raceAdditionalPricingItem?.pricingItemValues?.find((item: RaceCommercialItem) => item.id === idItem.racePricingItemValue);
          if (pricingItem) {
            let raceAdditionalPricingItemTemp: RaceAdditionalPricingItem = {...raceAdditionalPricingItem};
            raceAdditionalPricingItemTemp.pricingItemValues = [pricingItem];
            raceAdditionalItems.push(raceAdditionalPricingItemTemp);
          }
        } else {
          let raceAdditionalPricingItemTemp: RaceAdditionalPricingItem = {...raceAdditionalPricingItem};
          raceAdditionalItems.push(raceAdditionalPricingItemTemp);
        }
      }
    }
    this.registrations[index].raceAdditionalPricingItems = raceAdditionalItems;
    this.registerToRaceComponent?.recalculatePrices();
    this.registerToRaceComponent?.updateSummaryCompetitorItems();
  }

  onChangeEmail(email: string, index: number): void {
    this.registrations[index].email = email;
    this.registerToRaceComponent?.updateSummaryCompetitorItems();
  }

  isEmailUnique(index: number, email: string): boolean {
    let isEmailUnique: boolean = true;
    if (this.registrations.length > 1 && email && email !== '') {
      for (let i = 0; i < this.registrations.length; i++) {
        if (i !== index && this.registrations[i].email === email) {
          isEmailUnique = false;
          break;
        }
      }
    }
    return isEmailUnique;
  }

  areAllEmailsUnique(): boolean {
    const seen = new Set<string>();
    for (const registration of this.registrations) {
      if (registration.email) {
        if (seen.has(registration.email)) {
          return false;
        }
        seen.add(registration.email);
      }
    }
    return true;
  }

  addCompetitorRegistration() {
    let competitorRegistrationDto: CompetitorRegistrationDto = new CompetitorRegistrationDto();
    competitorRegistrationDto.raceCommercialItem = {...this.currentRaceCommercialItem};
    this.registrations.push(competitorRegistrationDto);
  }

  setDefaultRegistrationFee(index: number) {
    this.registrations[index].raceCommercialItem = this.currentRaceCommercialItem;
  }

  findIndexByEmail(email: string) {
    return this.registrations.findIndex((item: CompetitorRegistrationDto) => item.email === email);
  }

  setDefaultAdditionalPricingItems(index: number) {
    if (this.registrations[index].raceAdditionalPricingItems) {
      let tempRaceAdditionalPricingItems: RaceAdditionalPricingItem[] = [...this.registrations[index].raceAdditionalPricingItems!];
      this.registrations[index].raceAdditionalPricingItems = [];
      for (let currentItem of tempRaceAdditionalPricingItems) {
        for (let defaultItem of this.raceAdditionalItems) {
          if (currentItem.id === defaultItem.id) {
            let tempDefaultItem = {...defaultItem};
            tempDefaultItem.pricingItemValues = [];
            this.registrations[index].raceAdditionalPricingItems!.push(tempDefaultItem);
            if (currentItem.pricingItemValues && defaultItem.pricingItemValues) {
              for (let currentPricingItem of currentItem!.pricingItemValues) {
                for (let defaultPricingItem of defaultItem!.pricingItemValues) {
                  if (currentPricingItem.id === defaultPricingItem.id) {
                    tempDefaultItem.pricingItemValues.push(defaultPricingItem);
                    break;
                  }
                }
              }
            }
          }
        }
      }
      this.registrations[index].raceAdditionalPricingItems!.forEach((item: RaceAdditionalPricingItem) => {
        item.priceWithDiscount = item.price;
      });
    }
  }

  setCalculatedRegistrationFee(index: number, raceCommercialItem: RaceCommercialItem) {
    this.registrations[index].raceCommercialItem = raceCommercialItem;
  }

  setCalculatedAdditionalPricingItems(index: number, additionalItemDiscounts: AdditionalItemDiscount[] | undefined) {
    if (this.registrations[index].raceAdditionalPricingItems && additionalItemDiscounts) {
      let tempRaceAdditionalPricingItems: RaceAdditionalPricingItem[] = [...this.registrations[index].raceAdditionalPricingItems!];
      this.registrations[index].raceAdditionalPricingItems = [];
      for (let currentItem of tempRaceAdditionalPricingItems) {
        for (let calculatedItem of additionalItemDiscounts) {
          if (currentItem.id === calculatedItem?.additionalPricingItem?.id) {
            let tempDefaultItem = {...calculatedItem.additionalPricingItem};
            tempDefaultItem.pricingItemValues = [];
            this.registrations[index].raceAdditionalPricingItems!.push(tempDefaultItem);
            if (currentItem.pricingItemValues && calculatedItem.additionalPricingItem?.pricingItemValues) {
              for (let currentPricingItem of currentItem!.pricingItemValues) {
                for (let calculatedPricingItem of calculatedItem!.additionalPricingItem?.pricingItemValues) {
                  if (currentPricingItem.id === calculatedPricingItem.id) {
                    tempDefaultItem.pricingItemValues.push(calculatedPricingItem);
                    break;
                  }
                }
              }
            }
          }
        }
      }
    }
  }


  // Check if this is a team registration
  isTeamRegistration(): boolean {
    let isTeamRegistration: boolean = false;
    if (this.raceSettings?.teamType && this.raceSettings?.teamType !== TeamType.SINGLE) {
      isTeamRegistration = true;
    }
    return isTeamRegistration;
  }
}
